import axios from 'axios';
import axiosRetry from 'axios-retry';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 seconds timeout
});

// Configure retry logic with exponential backoff
axiosRetry(api, {
  retries: 3, // Maximum 3 retry attempts
  retryDelay: (retryCount) => {
    // Exponential backoff: 1s, 2s, 4s
    return Math.pow(2, retryCount - 1) * 1000;
  },
  retryCondition: (error) => {
    // Retry on network errors or 5xx server errors
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || 
           (error.response && error.response.status >= 500);
  },
  onRetry: (retryCount, error, requestConfig) => {
    console.log(`Retrying request (${retryCount}/3):`, requestConfig.url);
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    // Return a rejected promise to allow component-level error handling
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  updateDeviceToken: (token) => api.post('/auth/device-token', { token })
};

// Dispatch API
export const dispatchAPI = {
  createDispatch: (data) => api.post('/dispatches', data),
  getDispatches: (params) => api.get('/dispatches', { params }),
  getDispatch: (id) => api.get(`/dispatches/${id}`),
  updateStatus: (id, status) => api.patch(`/dispatches/${id}/status`, { status }),
  assignDriver: (id, driverId) => api.patch(`/dispatches/${id}/assign`, { driverId }),
  updateLocation: (id, location) => api.patch(`/dispatches/${id}/location`, location),
  getRoute: (id) => api.get(`/dispatches/${id}/route`)
};

// Onboarding API (in-product setup checklist)
export const onboardingAPI = {
  get: () => api.get('/onboarding'),
  setDismissed: (dismissed) => api.patch('/onboarding', { dismissed })
};

export default api;
